'use client'

import React, { useCallback, useEffect, useState } from 'react'

import { DeleteOutlined, PictureOutlined, StarOutlined, UploadOutlined } from '@ant-design/icons'
import { IFsNodeSchema } from '@cloudike/web_files'
import {
  currentBoardIdSelector,
  currentBoardSelector,
  getFilteredBoardsSelector,
} from 'features/boards/model/selectors/boardsSelectors'
import { boardsActions, fetchAllBoardsThunk, moveBoardsThunk, copyBoardsThunk } from 'features/boards/model/slice/boardsSlice'
import { AddNewBoardModal } from 'features/boards/ui/AddNewBoardModal'
import {
  SelectTargetBoardToUploadModal,
  setUploadFilesForTargetBoardModal
} from 'features/boards/ui/SelectTargetBoardToUploadModal'
import {
  actions,
  setIsAddNewBoardModalOpen,
  setIsCreateTeamModalOpen,
  setIsJoinTeamModalOpen,
  setIsSelectTargetBoardToUploadModalOpen
} from 'features/common/model/slice/appSlice'
import { DashboardLayout as DashboardLayoutComponent } from 'features/common/ui/dashboard/Layout'
import { FilesUploadingWidget } from 'features/files-uploading/ui/FilesUploadingWidget'
import { familySelector } from 'features/team/model/selectors/teamSelectors'
import { TeamModalManager } from 'features/team/ui/TeamModalManager'
import { rootBoardIdSelector, userSelector, userSettingsSelector } from 'features/user/model/selectors/userSelectors'
import {
  fetchUserAvatarsThunk,
  fetchUserAvatarThunk,
  fetchUserDataThunk,
  fetchUserSettingsThunk,
  userActions
} from 'features/user/model/slice/userSlice'
import { ProtectedRoute } from 'features/user/ui/ProtectedRoute'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { ROUTES } from 'shared/constants/routesPaths'
import { Breadcrumb, MenuProps } from 'shared/ui'
import { getTreeLikeMenuFromNodes } from 'shared/utils/filesUtils'
import { useTranslation } from 'react-i18next'
import {
  moveAssetsThunk,
  copyAssetsThunk,
  assetsActions
} from 'features/assets/model/slice/assetsSlice'

import {
  subscribeToWSCommentsThunk,
  unsubscribeToWSCommentsThunk
} from '../../files-preview/model/slice/filesPreviewSlice'
import { useBoardsAssetsWS } from '../hooks'
import { clearSearchResults, setSearchInputValue } from '../model/slice/searchSlice'
import { useRouteTracking } from '../../../shared/hooks'

const generateBreadcrumbs = (boardsTree: any, currentBoard: IFsNodeSchema | null, navigate: any, t: (key: string) => string) => {
  if (window.location.pathname.includes(ROUTES.dashboard.allAssets)) {
    return [{ title: t("a_sidebar_allAssets"), id: 'all-assets' }]
  }

  if (!currentBoard || !boardsTree) {
    return []
  }

  const breadcrumbs: { title: string, id: string }[] = []

  const findPath = (nodes: any[], targetId: string, path: any[] = []): boolean => {
    for (const node of nodes) {
      const currentPath = [...path, { title: node.name, onClick: () => navigate(`${ROUTES.dashboard.board}/${node.id}`) }]

      if (node.key === targetId) {
        breadcrumbs.push(...currentPath)
        return true
      }

      if (node.children) {
        if (findPath(node.children, targetId, currentPath)) {
          return true
        }
      }
    }
    return false
  }

  findPath(boardsTree, currentBoard.id)
  return breadcrumbs
}

export const DashboardLayout = ({ children, isPublicLink = false }: { children?: React.ReactNode, isPublicLink?: boolean }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const currentBoard = useAppSelector(currentBoardSelector)
  const familyData = useAppSelector(familySelector)
  const userData = useAppSelector(userSelector)
  const [selectedKey, setSelectedKey] = useState<string>('all-assets')
  const isBoardInTrash = (item) => item.is_explicitly_trashed || item.is_trashed

  const teamName = familyData?.name
  const userSettings = useAppSelector(userSettingsSelector)

  const rootBoardId = useAppSelector(rootBoardIdSelector)

  const boards = useAppSelector(getFilteredBoardsSelector).filter((item) => !isBoardInTrash(item))

  useRouteTracking({
    onRouteChange: () => {
      dispatch(clearSearchResults())
      dispatch(setSearchInputValue(''))
    },
    enableLogging: process.env.NODE_ENV === 'development'
  })

  useBoardsAssetsWS()

  useEffect(() => {
    dispatch(subscribeToWSCommentsThunk())

    if(!userData?.id) {
      dispatch(fetchUserDataThunk()).unwrap()
        .then(() => {
          return dispatch(fetchUserAvatarsThunk())
            .unwrap()
        }).then(() => {
          dispatch(fetchUserAvatarThunk())
        })
    } else {
      dispatch(fetchUserAvatarsThunk())
        .unwrap().then(() => {
          dispatch(fetchUserAvatarThunk())
        })
    }

    return () => {
      dispatch(unsubscribeToWSCommentsThunk())
    }
  }, [])

  useEffect(() => {
    if (!isPublicLink) {
      dispatch(fetchAllBoardsThunk())
    }
  }, [isPublicLink, rootBoardId])

  const isAllAssetsPage = pathname === '/dashboard' || pathname === '/dashboard/all-assets'

  const handleAssetDropOnBoard = useCallback(async (assetIds: string[], boardId: string) => {
    if (assetIds.length > 0 && boardId) {
      if (isAllAssetsPage) {
        await dispatch(copyAssetsThunk({ ids: assetIds, destinationId: boardId })).unwrap()
        dispatch(assetsActions.unselectAll())
      } else {
        await dispatch(moveAssetsThunk({ ids: assetIds, destinationId: boardId })).unwrap()
        dispatch(assetsActions.unselectAll())
      }
    }
  }, [dispatch, isAllAssetsPage])

  const handleBoardDropOnBoard = useCallback(async (boardIds: string[], targetBoardId: string) => {
    if (boardIds.length > 0 && targetBoardId) {
      if (isAllAssetsPage) {
        await dispatch(copyBoardsThunk({ ids: boardIds, destinationId: targetBoardId })).unwrap()
        dispatch(boardsActions.unselectAll())
      } else {
        await dispatch(moveBoardsThunk({ ids: boardIds, destinationId: targetBoardId })).unwrap()
        dispatch(boardsActions.unselectAll())
      }
    }
  }, [dispatch, isAllAssetsPage])

  const menuItems: MenuProps['items'] = [
    {
      key: 'all-assets',
      icon: <PictureOutlined />,
      label: t("a_sidebar_allAssets"),
      onClick: () => {
        navigate(ROUTES.dashboard.allAssets)
      }
    },
    // {
    //   key: 'recently-uploaded',
    //   icon: <ClockCircleOutlined />,
    //   label: 'Recently uploaded',
    //   onClick: () => {
    //     navigate(ROUTES.dashboard.recentlyUploaded)
    //   }
    // },
    {
      key: 'my-uploads',
      icon: <UploadOutlined />,
      label: t("a_sidebar_myUploads"),
      onClick: () => {
        navigate(ROUTES.dashboard.myUploads)
      }
    },
    {
      key: 'favorites',
      icon: <StarOutlined />,
      label: t("a_sidebar_favorites"),
      onClick: () => {
        navigate(ROUTES.dashboard.favorites)
      }
    },
    {
      key: 'recently-deleted',
      icon: <DeleteOutlined />,
      label: t("a_sidebar_recentlyDeleted"),
      onClick: () => {
        navigate(ROUTES.dashboard.recentlyDeleted)
      }
    }
  ]

  // Определяем активный пункт меню на основе текущего пути
  useEffect(() => {
    const pathParts = pathname.split('/')
    const path = pathParts[pathParts.length - 1] || ''

    if (path === 'dashboard') {
      setSelectedKey('all-assets')
    } else {
      setSelectedKey(path)
    }
  }, [pathname])

  useEffect(() => {
    if (userData?.family_user_id) {
      dispatch(fetchUserSettingsThunk())
    }
  }, [userData?.family_user_id])

  useEffect(() => {
    if (!rootBoardId && !isPublicLink) {
      dispatch(fetchAllBoardsThunk())
    }
  }, [rootBoardId])

  useEffect(() => {
    if (userSettings && userSettings.length > 0) {
      const [damFS] = userSettings.filter((item: any) => item.path === 'dam.fs')
      const rootBoardId = damFS?.value.boards_dir_id || ''
      const assetRootBoardId = damFS?.value.assets_dir_id || ''
      dispatch(userActions.setDefaultRoots({ rootBoardId, assetRootBoardId }))
    }
  }, [userSettings])

  const handleUpload = (files) => {
    if (files && files.length > 0) {
      setUploadFilesForTargetBoardModal(files)
      dispatch(setIsSelectTargetBoardToUploadModalOpen(true))
    }
  }

  const handleAddBoard = () => {
    dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: null }))
    dispatch(setIsAddNewBoardModalOpen(true))
    dispatch(boardsActions.setSelectedParentId(null))
  }

  const handleOpenBoard = (item: any) => {
    if(!item || !item.id) return

    navigate(`${ROUTES.dashboard.board}/${item.id}`)
  }

  const boardsMenuItemsWithClick = getTreeLikeMenuFromNodes(boards, handleOpenBoard)

  useEffect(() => {
    return () => {
      dispatch(setIsCreateTeamModalOpen(false))
      dispatch(setIsJoinTeamModalOpen(false))
    }
  }, [])

  return (
    <ProtectedRoute>
      <DashboardLayoutComponent
        teamName={teamName}
        menuItems={menuItems}
        boardsItems={boardsMenuItemsWithClick}
        selectedKeys={[selectedKey]}
        onUpload={handleUpload}
        onAddBoard={handleAddBoard}
        onOpenBoard={handleOpenBoard}
        onAssetDropOnBoard={handleAssetDropOnBoard}
        onBoardDropOnBoard={handleBoardDropOnBoard}
        breadcrumbs={<Breadcrumb items={generateBreadcrumbs(boardsMenuItemsWithClick, currentBoard, navigate, t)} />}
        isPublicLink={isPublicLink}
      >
        {children || <Outlet />}
      </DashboardLayoutComponent>

      <FilesUploadingWidget />

      <SelectTargetBoardToUploadModal boards={boards} />

      <AddNewBoardModal />

      <TeamModalManager />
    </ProtectedRoute>
  )
}
